import { initializeApp } from 'firebase/app';
import { initializeFirestore, doc, setDoc, getDoc, collection, collectionGroup, getDocs, addDoc, query, orderBy, where, writeBatch, deleteDoc, serverTimestamp, limit } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signOut, onAuthStateChanged, deleteUser, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { DEFAULT_COA } from './coa';

// Replace with your Firebase config from the Firebase Console
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
// Force long-polling: Firestore's default WebChannel transport fails with HTTP 400
// on larger writes (e.g. posting a full statement of journals) behind some networks.
const db = initializeFirestore(app, { experimentalForceLongPolling: true });
const auth = getAuth(app);
const storage = getStorage(app);

// ═══ AUDIT TRAIL ═══
// Append-only record of every change to the books, for audit-grade traceability.
// Stored per project at projects/{id}/auditLog. Never edited or deleted.

// Best-effort actor identity. Until real auth is enforced this falls back to a
// clear placeholder so the trail is honest about not yet knowing the user.
export function currentActor() {
  const u = auth.currentUser;
  return (u && (u.email || u.uid)) || 'unauthenticated (local)';
}

// Write one audit entry. `entry` = { action, summary, details? }.
// Returns a batch-ready writer if a batch is supplied (so it can be atomic with
// the change it records); otherwise commits on its own.
export function auditEntryData(entry) {
  return {
    action: entry.action || 'unknown',
    summary: entry.summary || '',
    details: entry.details || null,
    actor: currentActor(),
    at: serverTimestamp(),
  };
}

export async function logAudit(projectId, entry) {
  await addDoc(collection(db, 'projects', projectId, 'auditLog'), auditEntryData(entry));
}

export async function loadAuditLog(projectId, max) {
  const q = query(collection(db, 'projects', projectId, 'auditLog'), orderBy('at', 'desc'), limit(max || 200));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ═══ AUTH & ROLES ═══
// Roles: 'admin' | 'preparer' | 'reviewer' | 'readonly'. Stored at users/{uid}.
// NOTE: requires Email/Password sign-in enabled in the Firebase console.

export function onAuth(cb) {
  return onAuthStateChanged(auth, cb);
}

export async function signIn(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

// Send a Firebase password-reset email. The message contains a secure, expiring
// link to Firebase's hosted reset page (no extra backend needed). Callers should
// show a generic confirmation regardless of outcome so we never reveal whether an
// address is registered.
export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, (email || '').trim());
}

// Public self-registration for a stokvel chairperson (or a member arriving via an
// invite link). Creates the Firebase Auth account, sends the verification email,
// and stamps the identity record as `stokvelOnly` so the app shows ONLY the stokvel
// product to them — the accounting/reconciliation side stays provisioned-only.
// The app keeps them signed in but gates access behind email verification.
export async function registerStokvelUser(email, password, name, referral) {
  const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
  const user = cred.user;
  try { await sendEmailVerification(user); } catch (e) { /* non-fatal: they can resend */ }
  // Partner referral code (e.g. a code we gave Women in Stokvels) — normalised and
  // stamped on the profile at signup so we can attribute the account and pay incentives.
  const ref = (referral || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  try {
    await setDoc(doc(db, 'users', user.uid), {
      email: (email || '').toLowerCase().trim(),
      name: (name || '').trim(),
      stokvelOnly: true,
      ...(ref ? { referralCode: ref, referredAt: serverTimestamp() } : {}),
      createdAt: serverTimestamp(),
    }, { merge: true });
  } catch (e) { /* non-fatal: ensureUserRecord will backfill on next load */ }
  return user;
}

// Resend the verification email to the currently signed-in (unverified) user.
export async function resendVerification() {
  if (auth.currentUser) await sendEmailVerification(auth.currentUser);
}

// Re-check the signed-in user's verified state against the server (used by the
// "I've verified" button so we don't rely on a stale token).
export async function refreshEmailVerified() {
  if (!auth.currentUser) return false;
  await auth.currentUser.reload();
  return !!auth.currentUser.emailVerified;
}

// Read a user's identity/profile record (carries the `stokvelOnly` flag that scopes
// what product they see). Returns {} when there is no record yet.
export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() : {};
}

// Join a stokvel with an invite code. A new member cannot add themselves to the
// members map under the security rules, so this goes through the authenticated
// Cloud Function (admin SDK), which validates the code and links the caller.
export async function joinStokvelByCode(code) {
  const BASE = process.env.REACT_APP_ENGINE_URL ?? 'http://localhost:3001';
  const res = await fetch(BASE + '/api/stokvel/join', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ code: String(code || '').toUpperCase().replace(/[^A-Z0-9]/g, '') }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data && data.error) || ('Could not join the group (' + res.status + ')'));
  return data; // { stokvelId, name, status: 'pending' | 'active', already }
}

// Chair/approver approves a pending member — assigns role + permissions, activates them.
export async function approveJoinRequest(stokvelId, uid, role, perms) {
  const BASE = process.env.REACT_APP_ENGINE_URL ?? 'http://localhost:3001';
  const res = await fetch(BASE + '/api/stokvel/approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ stokvelId, uid, role, perms }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data && data.error) || ('Could not approve the member (' + res.status + ')'));
  return data;
}

// Chair/approver declines a pending join request.
export async function rejectJoinRequest(stokvelId, uid) {
  const BASE = process.env.REACT_APP_ENGINE_URL ?? 'http://localhost:3001';
  const res = await fetch(BASE + '/api/stokvel/reject', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ stokvelId, uid }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data && data.error) || ('Could not decline the request (' + res.status + ')'));
  return data;
}

// Every pending join request for the signed-in user — powers the waiting room.
export async function listMyPendingJoins(uid) {
  const snap = await getDocs(query(collectionGroup(db, 'joinRequests'), where('uid', '==', uid)));
  return snap.docs.map((d) => ({ id: d.id, stokvelId: d.ref.parent.parent ? d.ref.parent.parent.id : null, ...d.data() }));
}

// Pending join requests for a stokvel — for the chair/approver to action.
export async function listJoinRequests(stokvelId) {
  const snap = await getDocs(collection(db, 'stokvels', stokvelId, 'joinRequests'));
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
}

export async function signOutUser() {
  await signOut(auth);
}

// Permanently delete the signed-in user's account (Apple/Play require an in-app path).
// Firebase requires a recent login to delete, so we re-authenticate with the password
// first. We best-effort remove the user's own profile doc; shared group records remain
// as group history per the Account & Data Deletion policy.
export async function deleteUserAccount(password) {
  const u = auth.currentUser;
  if (!u) throw new Error('You are not signed in.');
  if (u.email && password) {
    const cred = EmailAuthProvider.credential(u.email, password);
    await reauthenticateWithCredential(u, cred);
  }
  try { await deleteDoc(doc(db, 'users', u.uid)); } catch (e) { /* profile doc may not exist */ }
  await deleteUser(u);
}

// Authorization header carrying the current user's Firebase ID token, so the
// server-side endpoints (extraction proxy, invoice email) can verify the caller and
// reject anonymous requests. Returns {} when signed out.
export async function authHeader() {
  const u = auth.currentUser;
  if (!u) return {};
  try {
    return { Authorization: 'Bearer ' + (await u.getIdToken()) };
  } catch (e) {
    return {};
  }
}

// Ensure the user has an identity record (email registry) so admins can find and
// add them to companies. Access is governed PER-PROJECT by membership, not this doc.
export async function ensureUserRecord(uid, email) {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  const lower = (email || '').toLowerCase();
  if (!snap.exists()) {
    await setDoc(ref, { email: lower, createdAt: serverTimestamp() });
  } else if (snap.data().email !== lower && lower) {
    await setDoc(ref, { email: lower }, { merge: true });
  }
  return snap.exists() ? snap.data() : { email: lower };
}

// Find a user by email so an admin can grant them access (they must have signed in once).
export async function findUserByEmail(email) {
  const q = query(collection(db, 'users'), where('email', '==', (email || '').toLowerCase().trim()));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { uid: d.id, ...d.data() };
}

// ═══ PROJECT OPERATIONS ═══

export async function saveProject(projectId, data) {
  await setDoc(doc(db, 'projects', projectId), {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function loadProject(projectId) {
  const snap = await getDoc(doc(db, 'projects', projectId));
  return snap.exists() ? snap.data() : null;
}

export async function listProjects() {
  const snap = await getDocs(collection(db, 'projects'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ═══ MEMBERSHIP / PER-COMPANY ACCESS ═══
// Each project carries members: { [uid]: { role, email } } and a memberUids array
// (for querying + security rules). Roles: admin | preparer | reviewer | readonly | auditor.
// A company's users only see their company; KPMG auditors are added as 'auditor'
// (read + export) to the specific companies they audit.

// Projects this user may see. Strict, security-rules-compatible query: returns
// only companies where this user is a member (memberUids contains their uid).
// A full-collection read is denied by the deployed rules; this array-contains
// query is allowed because every returned doc satisfies the per-doc read rule.
// (Legacy projects must already be claimed — see claimProjectIfUnclaimed — before
// the rules are deployed, or the owner won't see them to claim them.)
export async function listProjectsForUser(uid) {
  const q = query(collection(db, 'projects'), where('memberUids', 'array-contains', uid));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addProjectMember(projectId, uid, email, role) {
  const ref = doc(db, 'projects', projectId);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const members = { ...(data.members || {}), [uid]: { role, email: (email || '').toLowerCase() } };
  await setDoc(ref, { members, memberUids: Object.keys(members), updatedAt: serverTimestamp() }, { merge: true });
  await logAudit(projectId, { action: 'add_member', summary: 'Granted ' + role + ' access to ' + (email || uid), details: { uid, email: (email || '').toLowerCase(), role } });
}

export async function removeProjectMember(projectId, uid) {
  const ref = doc(db, 'projects', projectId);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const members = { ...(data.members || {}) };
  const removed = members[uid];
  delete members[uid];
  await setDoc(ref, { members, memberUids: Object.keys(members), updatedAt: serverTimestamp() }, { merge: true });
  await logAudit(projectId, { action: 'remove_member', summary: 'Removed access for ' + ((removed && removed.email) || uid), details: { uid } });
}

// One-time migration helper: claim a legacy project (no members) as its admin.
export async function claimProjectIfUnclaimed(projectId, uid, email) {
  const ref = doc(db, 'projects', projectId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  const data = snap.data();
  if (!data.memberUids || data.memberUids.length === 0) {
    await addProjectMember(projectId, uid, email, 'admin');
    return true;
  }
  return false;
}

// ═══ CHAT HISTORY ═══

export async function saveChatMessage(projectId, message) {
  await addDoc(collection(db, 'projects', projectId, 'chat'), {
    ...message,
    timestamp: serverTimestamp(),
  });
}

export async function loadChatHistory(projectId) {
  const q = query(
    collection(db, 'projects', projectId, 'chat'),
    orderBy('timestamp', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data());
}

// ═══ FILE STORAGE ═══

export async function uploadStatement(projectId, file, month) {
  const path = `projects/${projectId}/statements/${month}_${file.name}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return { path, url, name: file.name };
}

export async function listStatements(projectId) {
  const listRef = ref(storage, `projects/${projectId}/statements`);
  const res = await listAll(listRef);
  const files = await Promise.all(
    res.items.map(async (item) => ({
      name: item.name,
      url: await getDownloadURL(item),
    }))
  );
  return files;
}

export async function uploadOutput(projectId, blob, filename) {
  const path = `projects/${projectId}/outputs/${filename}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob);
  return await getDownloadURL(storageRef);
}

// ── Stokvel receipts/invoices (paid "audit trail" add-on) ──
// Files live under stokvels/{id}/receipts/ so Storage rules can scope access to
// that stokvel's members. `blob` is the (already-compressed) file/blob to store.
export async function uploadStokvelReceipt(stokvelId, storageKey, blob, contentType) {
  const path = `stokvels/${stokvelId}/receipts/${storageKey}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, contentType ? { contentType } : undefined);
  const url = await getDownloadURL(storageRef);
  return { path, url };
}

export async function deleteStokvelReceipt(path) {
  try { await deleteObject(ref(storage, path)); } catch (e) { /* already gone — non-fatal */ }
}

// ═══ TRANSACTIONS ═══

export async function saveTransactions(projectId, month, transactions) {
  await setDoc(doc(db, 'projects', projectId, 'transactions', month), {
    transactions,
    updatedAt: serverTimestamp(),
  });
}

export async function loadTransactions(projectId, month) {
  const snap = await getDoc(doc(db, 'projects', projectId, 'transactions', month));
  return snap.exists() ? snap.data().transactions : [];
}

export async function loadAllTransactions(projectId) {
  const snap = await getDocs(collection(db, 'projects', projectId, 'transactions'));
  const all = [];
  snap.docs.forEach(d => {
    const data = d.data();
    if (data.transactions) all.push(...data.transactions);
  });
  return all.sort((a, b) => new Date(a.date) - new Date(b.date));
}

// ═══ AUTH ═══

export async function login(email, password) {
  return await signInWithEmailAndPassword(auth, email, password);
}

export async function logout() {
  return await signOut(auth);
}

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

// ═══ COMPANY DATA ═══
// Company profiles, shareholders, balances and transactions live per-tenant in
// Firestore (projects/{id} + subcollections), created through the app and
// governed by the membership security rules. No client data is hardcoded here.

// ═══ CHART OF ACCOUNTS ═══

export async function seedCOA(projectId, overrides) {
  const accounts = overrides && overrides.length ? overrides : DEFAULT_COA;
  const batch = writeBatch(db);
  accounts.forEach((a) => {
    batch.set(doc(db, 'projects', projectId, 'coa', a.code), a);
  });
  await batch.commit();
}

export async function loadCOA(projectId) {
  const snap = await getDocs(collection(db, 'projects', projectId, 'coa'));
  return snap.docs.map((d) => d.data()).sort((a, b) => a.code.localeCompare(b.code));
}

export async function upsertAccount(projectId, account) {
  const ref = doc(db, 'projects', projectId, 'coa', account.code);
  const prev = await getDoc(ref);
  const existed = prev.exists();
  await setDoc(ref, account, { merge: true });
  await logAudit(projectId, {
    action: existed ? 'update_account' : 'create_account',
    summary: (existed ? 'Updated' : 'Created') + ' account ' + account.code + ' — ' + (account.name || ''),
    details: { before: existed ? prev.data() : null, after: account },
  });
}

export async function deleteAccount(projectId, code) {
  await deleteDoc(doc(db, 'projects', projectId, 'coa', code));
}

// ═══ LEDGER SETTINGS / PERIOD LOCK ═══
// A single "lock date": any journal dated on/before it is in a closed period and
// cannot be posted, changed, or reversed. Standard practice for audit-grade books.

export async function loadLedgerSettings(projectId) {
  const snap = await getDoc(doc(db, 'projects', projectId, 'meta', 'ledger'));
  return snap.exists() ? snap.data() : {};
}

export async function setLockDate(projectId, lockDate) {
  await setDoc(doc(db, 'projects', projectId, 'meta', 'ledger'),
    { lockDate: lockDate || null, updatedAt: serverTimestamp(), updatedBy: currentActor() }, { merge: true });
  await logAudit(projectId, {
    action: 'set_lock_date',
    summary: lockDate ? ('Locked all periods on/before ' + lockDate) : 'Cleared the period lock',
    details: { lockDate: lockDate || null },
  });
}

async function assertNotLocked(projectId, date) {
  const s = await loadLedgerSettings(projectId);
  if (s && s.lockDate && date && date <= s.lockDate) {
    throw new Error('This period is locked (on/before ' + s.lockDate + '). Unlock it before posting or changing entries dated in it.');
  }
}

// ═══ JOURNAL ENTRIES (double-entry) ═══
// Each journal: { date, narration, source, lines: [{accountCode, dr, cr}], postedAt }
// Invariant: sum(line.dr) === sum(line.cr) for every entry.

export async function postJournal(projectId, journal) {
  const total = (journal.lines || []).reduce(
    (acc, l) => ({ dr: acc.dr + (Number(l.dr) || 0), cr: acc.cr + (Number(l.cr) || 0) }),
    { dr: 0, cr: 0 }
  );
  if (Math.abs(total.dr - total.cr) > 0.01) {
    throw new Error('Journal does not balance: dr=' + total.dr.toFixed(2) + ' cr=' + total.cr.toFixed(2));
  }
  if (!journal.date) throw new Error('Journal date is required');
  await assertNotLocked(projectId, journal.date);
  const ref = await addDoc(collection(db, 'projects', projectId, 'journals'), {
    ...journal,
    postedAt: serverTimestamp(),
  });
  await logAudit(projectId, {
    action: 'post_journal',
    summary: 'Posted journal' + (journal.narration ? ' — ' + journal.narration : '') + ' (' + (journal.date || '') + ')',
    details: { journalId: ref.id, journal },
  });
  return ref.id;
}

// Atomic, all-or-nothing post of many journals + one audit entry, in a single
// batch. Either every entry is written or none are — no partial posts.
export async function postJournals(projectId, journals) {
  // Validate everything BEFORE writing anything.
  journals.forEach((j, i) => {
    const t = (j.lines || []).reduce((a, l) => ({ dr: a.dr + (Number(l.dr) || 0), cr: a.cr + (Number(l.cr) || 0) }), { dr: 0, cr: 0 });
    if (Math.abs(t.dr - t.cr) > 0.01) throw new Error('Journal ' + (i + 1) + ' does not balance: dr=' + t.dr.toFixed(2) + ' cr=' + t.cr.toFixed(2));
    if (!j.date) throw new Error('Journal ' + (i + 1) + ' is missing a date');
  });
  // Firestore batches are capped at 500 ops; reserve one for the audit entry.
  if (journals.length > 499) {
    throw new Error('Too many journals for one atomic post (' + journals.length + '). Post in batches of 499 or fewer.');
  }
  // Reject the whole batch if any entry falls in a locked period.
  const settings = await loadLedgerSettings(projectId);
  if (settings.lockDate) {
    const locked = journals.find((j) => j.date && j.date <= settings.lockDate);
    if (locked) throw new Error('One or more entries fall in a locked period (on/before ' + settings.lockDate + '). Unlock it before posting.');
  }
  const batch = writeBatch(db);
  const ids = [];
  journals.forEach((j) => {
    const ref = doc(collection(db, 'projects', projectId, 'journals'));
    batch.set(ref, { ...j, postedAt: serverTimestamp() });
    ids.push(ref.id);
  });
  batch.set(doc(collection(db, 'projects', projectId, 'auditLog')), auditEntryData({
    action: 'post_journals',
    summary: 'Posted ' + journals.length + ' journal(s)',
    details: { count: journals.length, ids },
  }));
  await batch.commit();
  return ids;
}

export async function loadJournals(projectId, opts) {
  const o = opts || {};
  const q = query(collection(db, 'projects', projectId, 'journals'), orderBy('date', 'asc'));
  const snap = await getDocs(q);
  let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (o.from) rows = rows.filter((r) => r.date >= o.from);
  if (o.to) rows = rows.filter((r) => r.date <= o.to);
  return rows;
}

export async function updateJournal(projectId, journalId, journal) {
  const total = (journal.lines || []).reduce(
    (acc, l) => ({ dr: acc.dr + (Number(l.dr) || 0), cr: acc.cr + (Number(l.cr) || 0) }),
    { dr: 0, cr: 0 }
  );
  if (Math.abs(total.dr - total.cr) > 0.01) {
    throw new Error('Journal does not balance: dr=' + total.dr.toFixed(2) + ' cr=' + total.cr.toFixed(2));
  }
  if (!journal.date) throw new Error('Journal date is required');
  const ref = doc(db, 'projects', projectId, 'journals', journalId);
  const prev = await getDoc(ref);
  const before = prev.exists() ? prev.data() : null;
  // merge:true overwrites date/narration/lines while preserving postedAt + source.
  await setDoc(ref, { ...journal, updatedAt: serverTimestamp() }, { merge: true });
  await logAudit(projectId, {
    action: 'update_journal',
    summary: 'Edited journal ' + journalId + (journal.narration ? ' — ' + journal.narration : ''),
    details: { journalId, before, after: journal },
  });
}

export async function deleteJournal(projectId, journalId) {
  const ref = doc(db, 'projects', projectId, 'journals', journalId);
  const prev = await getDoc(ref);
  const before = prev.exists() ? prev.data() : null;
  await deleteDoc(ref);
  await logAudit(projectId, {
    action: 'delete_journal',
    summary: 'Deleted journal ' + journalId + (before && before.narration ? ' — ' + before.narration : ''),
    details: { journalId, before },
  });
}

// Append a reversing entry (dr/cr swapped) that nets the original to zero in the
// same period. The original journal is NEVER modified — true append-only ledger.
// Returns the new reversal entry's id.
export async function reverseJournal(projectId, journalId, reason) {
  const ref = doc(db, 'projects', projectId, 'journals', journalId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Journal not found: ' + journalId);
  const orig = snap.data();
  if (orig.reverses) throw new Error('That entry is itself a reversal — it cannot be reversed.');
  await assertNotLocked(projectId, orig.date);
  const lines = (orig.lines || []).map((l) => ({
    accountCode: l.accountCode,
    accountName: l.accountName || '',
    dr: Number(l.cr) || 0,   // swap
    cr: Number(l.dr) || 0,
  }));
  const revRef = await addDoc(collection(db, 'projects', projectId, 'journals'), {
    date: orig.date,
    narration: 'Reversal: ' + (orig.narration || ''),
    lines,
    reverses: journalId,
    reversalReason: reason || null,
    source: orig.source || null,
    postedAt: serverTimestamp(),
  });
  await logAudit(projectId, {
    action: 'reverse_journal',
    summary: 'Reversed journal ' + journalId + (orig.narration ? ' — ' + orig.narration : '') + (reason ? ' (' + reason + ')' : ''),
    details: { reverses: journalId, reversalId: revRef.id, reason: reason || null, original: orig },
  });
  return revRef.id;
}

// ═══ OPENING BALANCES ═══
// Stored per period as a doc keyed by period start date (e.g. "2025-03-01").
// Shape: { periodStart, balances: { [accountCode]: { dr, cr } } }

export async function saveOpeningBalances(projectId, periodStart, balances) {
  await setDoc(doc(db, 'projects', projectId, 'openingBalances', periodStart), {
    periodStart,
    balances,
    updatedAt: serverTimestamp(),
  });
}

export async function loadOpeningBalances(projectId, periodStart) {
  const snap = await getDoc(doc(db, 'projects', projectId, 'openingBalances', periodStart));
  return snap.exists() ? snap.data().balances : {};
}

// Pick the most recent opening-balance set saved on or before `date`.
// Lets a report load the correct opening position for whatever period is chosen,
// instead of being tied to a fixed project-level year start.
export async function loadOpeningBalancesAsAt(projectId, date) {
  const snap = await getDocs(collection(db, 'projects', projectId, 'openingBalances'));
  let best = null; // { periodStart, balances }
  snap.docs.forEach((d) => {
    const ps = d.id; // doc id IS the period start date string (e.g. "2025-03-01")
    if (date && ps > date) return; // ignore sets that start after the report period
    if (!best || ps > best.periodStart) best = { periodStart: ps, balances: d.data().balances || {} };
  });
  return best ? best.balances : {};
}

// ═══ INVENTORY (per-project: items, suppliers, movements) ═══

export async function loadInvItems(projectId) {
  const snap = await getDocs(collection(db, 'projects', projectId, 'invItems'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function addInvItem(projectId, item) {
  const ref = await addDoc(collection(db, 'projects', projectId, 'invItems'), { ...item, createdAt: serverTimestamp() });
  return ref.id;
}
export async function updateInvItem(projectId, id, patch) {
  await setDoc(doc(db, 'projects', projectId, 'invItems', id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}
export async function loadInvSuppliers(projectId) {
  const snap = await getDocs(collection(db, 'projects', projectId, 'invSuppliers'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function addInvSupplier(projectId, s) {
  const ref = await addDoc(collection(db, 'projects', projectId, 'invSuppliers'), { ...s, createdAt: serverTimestamp() });
  return ref.id;
}
export async function updateInvSupplier(projectId, id, patch) {
  await setDoc(doc(db, 'projects', projectId, 'invSuppliers', id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}
export async function deleteInvSupplier(projectId, id) {
  await deleteDoc(doc(db, 'projects', projectId, 'invSuppliers', id));
}
export async function loadInvMovements(projectId) {
  const snap = await getDocs(collection(db, 'projects', projectId, 'invMovements'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}
export async function addInvMovement(projectId, m) {
  const ref = await addDoc(collection(db, 'projects', projectId, 'invMovements'), { ...m, createdAt: serverTimestamp() });
  return ref.id;
}

// ═══ PAYROLL (per-project: employees, pay runs) ═══

export async function loadPayEmployees(projectId) {
  const snap = await getDocs(collection(db, 'projects', projectId, 'payEmployees'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function addPayEmployee(projectId, emp) {
  const ref = await addDoc(collection(db, 'projects', projectId, 'payEmployees'), { ...emp, createdAt: serverTimestamp() });
  return ref.id;
}
export async function updatePayEmployee(projectId, id, patch) {
  await setDoc(doc(db, 'projects', projectId, 'payEmployees', id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}
export async function deletePayEmployee(projectId, id) {
  await deleteDoc(doc(db, 'projects', projectId, 'payEmployees', id));
}
export async function loadPayRuns(projectId) {
  const snap = await getDocs(collection(db, 'projects', projectId, 'payRuns'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => String(b.period || '').localeCompare(String(a.period || '')));
}
export async function addPayRun(projectId, run) {
  const ref = await addDoc(collection(db, 'projects', projectId, 'payRuns'), { ...run, createdAt: serverTimestamp() });
  return ref.id;
}

// ═══ CATEGORISATION RULES (per-project categorisation memory) ═══
// One doc: projects/{id}/meta/categorisationRules → { rules: { [normDesc]: {accountCode, accountName} } }

export async function loadCategorisationRules(projectId) {
  const snap = await getDoc(doc(db, 'projects', projectId, 'meta', 'categorisationRules'));
  return snap.exists() ? (snap.data().rules || {}) : {};
}

export async function saveCategorisationRules(projectId, rules) {
  await setDoc(doc(db, 'projects', projectId, 'meta', 'categorisationRules'), {
    rules: rules || {},
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ═══ INVOICES (issued by the practice, top-level collection) ═══
// Schema:
// {
//   invoiceNumber, date, dueDate,
//   customerProjectId, customerSnapshot: {name, address, email, contact, vatNumber},
//   lines: [{ serviceCode, description, hours, rate, amount }],
//   subtotal, vatRate, vatAmount, total,
//   notes, status: 'draft'|'sent'|'paid'|'cancelled',
//   createdAt, updatedAt
// }

export async function saveInvoice(invoice) {
  const id = invoice.id || invoice.invoiceNumber;
  if (!id) throw new Error('Invoice needs an id or invoiceNumber');
  const { id: _ignore, ...data } = invoice;
  await setDoc(doc(db, 'invoices', id), {
    ...data,
    updatedAt: serverTimestamp(),
    createdAt: invoice.createdAt || serverTimestamp(),
  }, { merge: true });
  return id;
}

export async function loadInvoices() {
  const q = query(collection(db, 'invoices'), orderBy('date', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function deleteInvoice(invoiceId) {
  await deleteDoc(doc(db, 'invoices', invoiceId));
}

// Generate the next invoice number for a given year. Format: INV-YYYY-NNN.
export async function nextInvoiceNumber(year) {
  const y = year || new Date().getFullYear();
  const prefix = 'INV-' + y + '-';
  const snap = await getDocs(collection(db, 'invoices'));
  let max = 0;
  snap.docs.forEach((d) => {
    const num = d.id;
    if (typeof num === 'string' && num.startsWith(prefix)) {
      const n = parseInt(num.slice(prefix.length), 10);
      if (!isNaN(n) && n > max) max = n;
    }
  });
  const next = max + 1;
  return prefix + String(next).padStart(3, '0');
}

// ═══ CRM: PIPELINE LEADS (practice-level prospects, top-level collection) ═══
// Prospective accounting clients the practice is courting. Top-level (not
// company-scoped), like invoices. Active clients are projects; leads become a
// project when won.
export async function loadLeads() {
  const snap = await getDocs(collection(db, 'leads'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function saveLead(lead) {
  const id = lead.id || doc(collection(db, 'leads')).id;
  const { id: _ignore, ...data } = lead;
  await setDoc(doc(db, 'leads', id), {
    ...data,
    updatedAt: serverTimestamp(),
    createdAt: lead.createdAt || serverTimestamp(),
  }, { merge: true });
  return id;
}

export async function deleteLead(id) {
  await deleteDoc(doc(db, 'leads', id));
}

// Save CRM fields (status, contact, notes) onto a client company's project doc.
export async function saveProjectCrm(projectId, crm) {
  await setDoc(doc(db, 'projects', projectId), { crm, updatedAt: serverTimestamp() }, { merge: true });
}

// ═══ TASKS & CALENDAR (top-level: to-dos, meetings, reminders; team handoffs) ═══
export async function loadTasks() {
  const snap = await getDocs(collection(db, 'tasks'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function saveTask(task) {
  const id = task.id || doc(collection(db, 'tasks')).id;
  const { id: _ignore, ...data } = task;
  await setDoc(doc(db, 'tasks', id), { ...data, updatedAt: serverTimestamp(), createdAt: task.createdAt || serverTimestamp() }, { merge: true });
  return id;
}
export async function deleteTask(id) {
  await deleteDoc(doc(db, 'tasks', id));
}
// All known users (the email registry) — for assigning tasks to teammates.
export async function loadUsers() {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
}

export { db, auth, storage };
